import { observable, action, computed, makeObservable } from 'mobx';
import _ from 'lodash';
import apiActions from 'api/actions';

export class Lesson {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.title = data.title;
    this.order = data.order;
    this.description = data.description;
    this.video_url = data.video_url;
    this.video_url_thumnail = data.video_url_thumbnail;
    this.topic_id = data.topic_id;
    this.skill_id = data.skill_id;
    this.url_resources = data.url_resources;
  }

  get topic() {
    return this._root.TopicStore.find(this.topic_id);
  }

  get skill() {
    return this._root.SkillStore.find(this.skill_id);
  }

  get activities() {
    return this._root.ActivityStore.activitiesForSkill(this._root.sessionStore.currentUser.id, this.skill_id);
  }

  get assessmentActivities() {
    return this._root.AssessmentStore.forSkill(this.skill_id).map(assessment => assessment.activity);
  }

  completedActivityInstances(userId) {
    const activityIds = _.map(this.activities, 'id');
    return _.chain(this._root.ActivityExecutionStore.forUser(userId))
            .map('activityInstance')
            .compact()
            .filter(instance => _.includes(activityIds, instance.activity_id))
            .filter('completed_at')
            .orderBy('completed_at')
            .value()
  }

  lastCompletedActivityInstance(userId) {
    return _.last(this.completedActivityInstances(userId));
  }

  completedAssessmentInstances(userId) {
    const activityIds = _.chain(this.activities)
                         .filter('assessment_id')
                         .map('id')
                         .value();
    return _.chain(this._root.ActivityExecutionStore.forUser(userId))
            .map('activityInstance')
            .compact()
            .filter(instance => _.includes(activityIds, instance.activity_id))
            .filter('completed_at')
            .each(instance => this._root.AssessmentStore.fetchByActivityId(instance.activity_id))
            .orderBy('completed_at')
            .value();
  }

  lastCompletedAssessmentInstance(userId) {
    return _.last(this.completedAssessmentInstances(userId));
  }

  progressLogsForStudent(studentId) {
    return _.sortBy(this._root.LessonProgressLogStore.allForStudentAndLesson(studentId, this.id), 'inserted_at');
  }

  latestProgressLogsForStudent(studentId) {
    return _.chain(this.progressLogsForStudent(studentId))
            .sortBy('inserted_at')
            .last()
            .value();
  }
}

export class Topic {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.title = data.title;
    this.order = data.order;
    this.grade_id = data.grade_id;
    this.lessons = data.lessons.map(lessonData => new Lesson(lessonData, root))
  }

  get orderedLessons() {
    return _.sortBy(this.lessons, 'order')
  }

  get grade() {
    return this._root.GradeStore.find(this.grade_id);
  }
}

export default class TopicStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      requests: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      reset: action,
      fetchAll: action,
      refetch: action,
      fetchAllCompleted: computed,
    })

    this.root = root;
  }

  data = new Map();
  requests = new Map();

  add = data => {
    const topic = new Topic(data, this.root);
    this.data.set(topic.id, topic);
  }

  addMany = datas => {
    const merge = new Map();
    datas.forEach(data => {
      const topic = new Topic(data, this.root);
      merge.set(topic.id, topic);
    });
    this.data.merge(merge);
  }

  remove = id => {
    if (id && this.data.has(id)) this.data.delete(id);
  }

  find(id) {
    return this.data.get(id);
  }

  findLesson(id) {
    return _.find(this.allLessons, ['id', id]);
  }

  get all() {
    return Array.from(this.data.values());
  }

  get allLessons() {
    return _.flatMap(this.all, 'lessons');
  }

  forGrade(gradeId) {
    return _.chain(this.all)
            .filter(['grade_id', gradeId])
            .sortBy('order')
            .value();
  }

  reset = () => {
    this.data.clear();
    this.requests.clear();
  }

  fetchAll() {
    if (this.requests.get('all') === undefined) {
      this.requests.set('all', 'pending');
      return apiActions.v2RawGet('academics/topics').then((response) => {
        this.addMany(response.data);
        this.requests.set('all', 'complete');
      })
    }
  }

  refetch() {
    if (this.requests.get('all') !== undefined) {
      this.reset();
      return this.fetchAll();
    }
    return Promise.resolve();
  }

  get fetchAllCompleted() {
    return this.requests.get('all') === 'complete';
  }
}
